// Domain module extracted from lib/api.ts (frontend containment F1).
// Moved verbatim: endpoint paths, methods, payloads, parsing, types and export names
// are unchanged. Import via lib/api.ts, which re-exports this module.
import { request, BASE } from './client';

// ── George Messages ─────────────────────────────────────────────────────

export interface LookalikeProspect {
  company: string;
  person_name: string;
  role: string;
  email: string;
  why_fits: string;
  how_to_approach: string;
  // Set server-side at list-time: true when this email already exists
  // in contact_overrides for the tenant. Lets the UI render the ✓
  // added-state on refresh without holding it in memory.
  in_crm?: boolean;
}

export interface GeorgeMessage {
  id: string;
  client: string;
  msg_type: string;
  content: string;
  read: number;
  emailed: number;
  created_at: string;
  // dry_funnel briefings carry structured prospect data so the tile can
  // render per-prospect actions (Discuss / Prospect) instead of just
  // rendering the markdown.
  payload?: {
    kind?: string;
    opener?: string;
    prospects?: LookalikeProspect[];
  } | null;
}

export const getGeorgeMessages = (unreadOnly = false) =>
  request<GeorgeMessage[]>(`/george-messages${unreadOnly ? '?unread_only=true' : ''}`);

export const getGeorgeUnreadCount = () =>
  request<{ count: number }>('/george-messages/unread-count');

export const markGeorgeMessageRead = (id: string) =>
  request<{ ok: boolean }>(`/george-messages/${id}/read`, { method: 'POST' });

export const markAllGeorgeRead = () =>
  request<{ ok: boolean }>('/george-messages/mark-all-read', { method: 'POST' });

export const dismissGeorgeMessage = (id: string) =>
  request<{ ok: boolean }>(`/george-messages/${id}/dismiss`, { method: 'POST' });
export const undismissGeorgeMessage = (id: string) =>
  request<{ ok: boolean }>(`/george-messages/${id}/undismiss`, { method: 'POST' });

export const replyToGeorgeMessage = (id: string, message: string) =>
  request<{ id: string; content: string }>(`/george-messages/${id}/reply`, {
    method: 'POST', body: JSON.stringify({ message }),
  });

// Notifications
export const getNotifications = () =>
  request<{ id: number; type: string; title: string; body: string; read: boolean; created_at: string }[]>('/notifications');
export const markNotificationRead = (id: number) =>
  request<{ ok: boolean }>(`/notifications/${id}/read`, { method: 'POST' });
export const sendWelcome = () =>
  request<{ ok: boolean }>('/notifications/send-welcome', { method: 'POST' });

// Admin feature toggles
export interface FeatureToggles {
  outbound_eval: boolean;
  prospecting: boolean;
  call_attendance: boolean;
}
export const setAdminToggle = (feature: string, enabled: boolean) =>
  request<{ ok: boolean; toggles: FeatureToggles }>('/admin/toggles', {
    method: 'PUT', body: JSON.stringify({ feature, enabled }),
  });

// Prospecting credits
export interface ProspectingCredits {
  used: number;
  free: number;
  remaining: number;  // can be negative — overage is billed
  cost_per: number;   // price per generation past `free`, in USD
  billable: number;   // generations past `free` so far
  total_cost: number; // billable * cost_per, rounded to cents
}
export const getProspectingCredits = () =>
  request<ProspectingCredits>('/prospecting/credits');

// Lookalike-prospect discovery — runs the same engine as the dry_funnel
// briefing (Claude + Brave + Hunter/ZeroBounce). On-demand from the
// Prospecting page when the rep wants to expand the pipeline.
export interface LookalikeBriefResult {
  opener: string;
  markdown: string;
  prospects: LookalikeProspect[];
  domain_patterns: Record<string, string>;
  dropped_sections: string[];
}
export const discoverLookalikes = () =>
  request<LookalikeBriefResult>('/prospecting/discover-lookalikes', { method: 'POST' });

// Chat-first preselection: given the prospecting chat history, pick the
// strongest cold-outreach target (company + person + email) the
// conversation has converged on. Used by the chat "Draft email" button
// when the form's client/contact fields are empty.
export const preselectTarget = (chat_history: { role: string; content: string }[]) =>
  request<{ company: string; person_name: string; email: string; rationale: string }>(
    '/prospecting/preselect-target',
    { method: 'POST', body: JSON.stringify({ chat_history }) },
  );

// Billing
export const createCheckoutSession = (tier: number) =>
  request<{ url: string }>('/billing/create-checkout-session', {
    method: 'POST', body: JSON.stringify({ tier }),
  });
export const createPortalSession = () =>
  request<{ url: string }>('/billing/portal', { method: 'POST' });
export const resumeSubscription = () =>
  request<{ ok: boolean; subscription_id: string; ends_at: string }>('/billing/resume', { method: 'POST' });
export interface ReconcileResult {
  ok: boolean;
  reason?: string;
  before_tier: number;
  after_tier: number;
  price_id?: string;
  customer_id?: string;
  configured_prices?: string[];
  cancelled_duplicates?: string[];
}
export const reconcileSubscription = () =>
  request<ReconcileResult>('/billing/reconcile', { method: 'POST' });

// Voice preview
export const getVoicePreviewUrl = (voiceId: string) =>
  `${BASE}/george/voice-preview/${encodeURIComponent(voiceId)}`;

// Pronunciation test
export const getPronunciationTestUrl = (text: string) =>
  `${BASE}/george/pronunciation-test?text=${encodeURIComponent(text)}`;

// Voice credits
export const getVoiceCredits = () =>
  request<{ unlimited: boolean; remaining: number; total: number }>('/george/voice-credits');

// Calendar integration
export const getUpcomingEvents = (days = 7) =>
  request<{ events: Array<{
    id: string;
    summary: string;
    start: string;
    attendees: string[];
    george_invited: boolean;
    has_meeting_url: boolean;
    has_client?: boolean;
    has_external?: boolean;
    // 'client' = matched a known client domain; 'external' = external attendees
    // but no matched client (prospects); 'internal' = org-side only. Strategy
    // calls are re-tagged as 'client' when a client is associated.
    call_type?: 'client' | 'external' | 'internal';
    matched_client?: string;
    invite_type?: 'manual' | 'auto' | null;
  }>; auto_invite: boolean; error?: string }>(`/george/calendar/upcoming?days=${days}`);
export const inviteGeorgeToEvent = (eventId: string) =>
  request<{ ok: boolean }>(`/george/calendar/invite/${encodeURIComponent(eventId)}`, { method: 'POST' });
export const removeGeorgeFromEvent = (eventId: string) =>
  request<{ ok: boolean }>(`/george/calendar/invite/${encodeURIComponent(eventId)}`, { method: 'DELETE' });
export const toggleAutoInvite = () =>
  request<{ auto_invite: boolean }>('/george/calendar/auto-invite', { method: 'POST' });

// Pre-call briefs
export interface CallBrief {
  event_id: string;
  client: string;
  brief_type: 'initial' | 'followup';
  content: string;
  generated_at: string;
}
export const getCallBrief = (eventId: string) =>
  request<{ brief: CallBrief | null }>(`/george/calls/${encodeURIComponent(eventId)}/brief`);
export const regenerateCallBrief = (eventId: string) =>
  request<{ brief: CallBrief }>(`/george/calls/${encodeURIComponent(eventId)}/brief/regenerate`, { method: 'POST' });

// Fetch the brief PDF (auth via cookie) and trigger a browser download.
// Generates the brief on the server if one doesn't exist yet.
export async function downloadCallBriefPdf(eventId: string): Promise<void> {
  const res = await fetch(`${BASE}/george/calls/${encodeURIComponent(eventId)}/brief.pdf`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Brief download failed (${res.status})`);
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="?([^"]+)"?/);
  const name = m ? m[1] : 'Call Prep.pdf';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Email send + schedule
export const sendEmail = (formData: FormData) =>
  fetch(`${BASE}/email/send`, { method: 'POST', credentials: 'include', body: formData }).then(async r => { if (!r.ok) { const b = await r.json().catch(() => null); throw new Error(b?.detail || `${r.status} ${r.statusText}`); } return r.json(); });
export const scheduleEmail = (formData: FormData) =>
  fetch(`${BASE}/email/schedule`, { method: 'POST', credentials: 'include', body: formData }).then(async r => { if (!r.ok) { const b = await r.json().catch(() => null); throw new Error(b?.detail || `${r.status} ${r.statusText}`); } return r.json(); });
export interface ScheduledEmail {
  id: string;
  send_at: string;
  to: string;
  subject: string;
  body?: string;
  cc?: string;
  client: string;
  status: string;
  created_at: string;
  sequence_id?: string;
  step?: number;
  strafe_job_id?: string;
  // Marketing-document IDs to attach at send time. Resolved against
  // the outreach catalog by call_scheduler.py.
  outreach_doc_ids?: string[];
  // User-uploaded custom file metadata. The actual bytes stay on the
  // server (base64 in scheduled_emails.file_data) — only name + size
  // ride to the client so the list view doesn't pull megabytes of
  // attachment payloads it never displays.
  custom_files?: { name: string; size: number }[];
}
export const getScheduledEmails = (filters?: {
  sequence_ids?: string[];
  strafe_job_id?: string;
  client?: string;
  status?: string;
}) => {
  const qs = new URLSearchParams();
  if (filters?.sequence_ids?.length) qs.set('sequence_ids', filters.sequence_ids.join(','));
  if (filters?.strafe_job_id) qs.set('strafe_job_id', filters.strafe_job_id);
  if (filters?.client) qs.set('client', filters.client);
  if (filters?.status) qs.set('status', filters.status);
  const q = qs.toString();
  return request<ScheduledEmail[]>(`/email/scheduled${q ? '?' + q : ''}`);
};
export const cancelScheduledEmail = (id: string) =>
  request<{ ok: boolean }>(`/email/scheduled/${id}`, { method: 'DELETE' });
export const editScheduledEmail = (
  id: string,
  data: Record<string, string | string[] | number[]>,
) =>
  request<{ ok: boolean }>(`/email/scheduled/${id}`, {
    method: 'PUT', body: JSON.stringify(data),
  });
export const uploadScheduledAttachments = (id: string, files: File[]) => {
  // Multipart so file bytes don't get base64-ed on the client. The
  // server's POST /attachments handler encodes once and appends to
  // file_data.
  const fd = new FormData();
  for (const f of files) fd.append('files', f);
  return fetch(`${BASE}/email/scheduled/${id}/attachments`, {
    method: 'POST', body: fd, credentials: 'include',
  }).then(async r => {
    if (!r.ok) {
      const b = await r.json().catch(() => null);
      throw new Error(b?.detail || `${r.status} ${r.statusText}`);
    }
    return r.json();
  }) as Promise<{ ok: boolean; added: number }>;
};
